import { safeStorage } from 'common/utils/safeStorage';
/**
 * WebAuthn Service for browser-based biometric authentication
 *
 * Provides fingerprint/Face ID authentication for web session lock.
 * Uses the Web Authentication API (WebAuthn) which is supported in modern browsers.
 */

const credentialStorageKey = 'doqit_webauthn_credential';

// Check if WebAuthn is supported in this browser
export const isWebAuthnSupported = (): boolean => {
  return !!(
    window.PublicKeyCredential
    && typeof window.PublicKeyCredential === 'function'
  );
};

// Check if platform authenticator (fingerprint/Face ID) is available
export const isPlatformAuthenticatorAvailable = async (): Promise<boolean> => {
  if (!isWebAuthnSupported()) {
    return false;
  }
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
};

// Generate a random challenge for WebAuthn operations
const generateChallenge = (): Uint8Array => {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return array;
};

// Convert ArrayBuffer to base64 string for storage
const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

// Convert base64 string back to ArrayBuffer
const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;

interface StoredCredential {
  credentialId: string;
  rawId: string;
  userId: string;
}

// Internal shape that includes the TTL field persisted to localStorage
interface PersistedCredential extends StoredCredential {
  expiresAt: number;
}

// Store credential in localStorage with a 30-day TTL
const storeCredential = (credential: StoredCredential): void => {
  const persisted: PersistedCredential = {
    ...credential,
    expiresAt: Date.now() + thirtyDaysMs,
  };
  safeStorage.setItem(credentialStorageKey, JSON.stringify(persisted));
};

// Get stored credential from localStorage; returns null if missing or expired
export const getStoredCredential = (): StoredCredential | null => {
  const stored = safeStorage.getItem(credentialStorageKey);
  if (!stored) return null;
  try {
    const parsed: PersistedCredential = JSON.parse(stored);
    if (!parsed.expiresAt || parsed.expiresAt < Date.now()) {
      safeStorage.removeItem(credentialStorageKey);
      return null;
    }
    // Return only the public fields so callers are unaffected by the TTL field
    const { credentialId, rawId, userId } = parsed;
    return { credentialId, rawId, userId };
  } catch {
    return null;
  }
};

// Remove stored credential
export const removeStoredCredential = (): void => {
  safeStorage.removeItem(credentialStorageKey);
};

// Register a new WebAuthn credential for the user
export const registerCredential = async (
  userId: string,
  userEmail: string,
  userName: string,
): Promise<{ success: boolean; credentialId?: string; error?: string }> => {
  if (!isWebAuthnSupported()) {
    return { success: false, error: 'WebAuthn is not supported in this browser' };
  }

  const available = await isPlatformAuthenticatorAvailable();
  if (!available) {
    return { success: false, error: 'No platform authenticator (fingerprint/Face ID) available' };
  }

  try {
    const challenge = generateChallenge();

    const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
      challenge,
      rp: {
        name: 'Doqit',
        id: window.location.hostname,
      },
      user: {
        id: new TextEncoder().encode(userId),
        name: userEmail,
        displayName: userName,
      },
      pubKeyCredParams: [
        { alg: -7, type: 'public-key' }, // ES256
        { alg: -257, type: 'public-key' }, // RS256
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform', // Use built-in authenticator (fingerprint/Face ID)
        userVerification: 'required',
        residentKey: 'preferred',
      },
      timeout: 60000,
      attestation: 'none',
    };

    const credential = await navigator.credentials.create({
      publicKey: publicKeyCredentialCreationOptions,
    }) as PublicKeyCredential;

    if (!credential) {
      return { success: false, error: 'Failed to create credential' };
    }

    const credentialId = arrayBufferToBase64(credential.rawId);

    // Store credential for later authentication
    storeCredential({
      credentialId,
      rawId: credentialId,
      userId,
    });

    return { success: true, credentialId };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    // Handle user cancellation gracefully
    if (errorMessage.includes('cancelled') || errorMessage.includes('canceled')) {
      return { success: false, error: 'Setup was cancelled' };
    }
    return { success: false, error: errorMessage };
  }
};

// Authenticate using stored WebAuthn credential
export const authenticate = async (): Promise<{ success: boolean; error?: string }> => {
  if (!isWebAuthnSupported()) {
    return { success: false, error: 'WebAuthn is not supported in this browser' };
  }

  const storedCredential = getStoredCredential();
  if (!storedCredential) {
    return { success: false, error: 'No credential found. Please set up fingerprint first.' };
  }

  try {
    const challenge = generateChallenge();

    const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
      challenge,
      allowCredentials: [
        {
          id: base64ToArrayBuffer(storedCredential.rawId),
          type: 'public-key',
          transports: ['internal'],
        },
      ],
      userVerification: 'required',
      timeout: 60000,
    };

    const assertion = await navigator.credentials.get({
      publicKey: publicKeyCredentialRequestOptions,
    }) as PublicKeyCredential;

    if (!assertion) {
      return { success: false, error: 'Authentication failed' };
    }

    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    // Handle user cancellation gracefully
    if (errorMessage.includes('cancelled') || errorMessage.includes('canceled')) {
      return { success: false, error: 'Authentication was cancelled' };
    }
    return { success: false, error: errorMessage };
  }
};

// Check if WebAuthn is set up for the current user
export const hasWebAuthnSetup = (): boolean => {
  return getStoredCredential() !== null;
};
